import type {
  BaseQueryApi,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import AuthService from '@services/AuthService'

import { getResponseMessageText, showSnackbarNotification } from '@helpers'
import { ExtraOptions, ServerError } from '@types'

const baseQuery = (args: FetchArgs, api: BaseQueryApi, extraOptions: ExtraOptions) => {
  // const isSlots = args.url.startsWith('api/slots/')

  return fetchBaseQuery({
    prepareHeaders: headers => {
      const token = AuthService.getToken()

      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      } else {
        console.warn('[RTK Query] No token available for request:', args.url)
      }

      if (extraOptions?.contentType) {
        headers.set('Content-Type', extraOptions.contentType)
      }

      return headers
    },
  })(args, api, extraOptions)
}

export const baseQueryWithNotifications: BaseQueryFn<
  FetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions
> = async (args, api, extraOptions) => {
  const isInIframe = window !== window.top
  const requestArgs = args as FetchArgs

  const result = await baseQuery(args, api, extraOptions)

  const status = result.meta?.response?.status
  const isValidRequest = requestArgs?.body || requestArgs?.method === 'DELETE' || result.error
  const resultErrorData = result.error?.data as ServerError
  const showNotification = (result.error || extraOptions?.showNotification) ?? true

  if (status === 401) {
    console.error('[RTK Query] 401 Unauthorized:', requestArgs.url)

    // В iframe режиме не делаем редирект на логин
    if (!isInIframe) {
      AuthService.doLogin()
    } else {
      console.warn(
        '[RTK Query] [baseQueryWithNotifications] Skipping login redirect in iframe mode - parent app should handle auth'
      )
    }
  }

  if (isValidRequest && showNotification) {
    showSnackbarNotification({
      text: getResponseMessageText(result.error, resultErrorData),
      options: {
        variant: result.error && !result.meta?.response?.ok ? 'error' : 'success',
        statusCode: result.meta?.response?.status,
        details: resultErrorData?.details,
        showCancel: true,
        requestArgs: {
          ...requestArgs,
          url: result.meta?.request.url,
        },
      },
    })
  }

  return result
}
